import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UIMessage } from 'ai';
import { apiFetch } from './api';
import { useMe } from './auth';

export const chatHistoryQueryKey = (caseId: string) => ['cases', caseId, 'messages'] as const;

export function useChatHistory(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: chatHistoryQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ messages: UIMessage[] }>(`/cases/${caseId}/messages`),
    select: (d) => d.messages,
    enabled: !!caseId && !!me.data?.user,
    // We don't want the chat to flicker / reset on focus refetches.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Ask the agent to generate the opening assistant message. Idempotent
 * server-side: if an assistant message already exists, this is a no-op.
 * On success we refetch the history so the new message renders.
 */
export function useChatKickoff(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: UIMessage | null }>(`/cases/${caseId}/chat/kickoff`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatHistoryQueryKey(caseId) }),
  });
}
